import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { sendOutreach } from '../utils/outreachApi';
import {
    listDeploymentsFromCloud,
    stageDeploymentInCloud,
    unstageDeploymentInCloud,
    sendDeploymentInCloud,
    listStagingArea,
    addStagingAreaRecipient,
    removeStagingAreaRecipient,
    setStagingAreaContext,
    fetchPlanUsage,
    acquireEmailSendLease,
    releaseEmailSendLease,
    previewDispatch,
    confirmDispatch,
    listDispatchCapabilities,
} from '../utils/cloudApi';
import { DEFAULT_CHANNEL, canonicalExecutionChannel } from '../utils/channelInventory';
import { canonicalControlChannel, productionChannelDisabledReason } from '../utils/channelControlApi';
import { reconcileStagedSurveys } from '../utils/lifecycleUtils';

// The staged recipients used to live here, in localStorage. sam-dbam never
// saw it, so it could not hold it to the plan's per-survey cap. It now lives in
// sam-dbam, keyed by account, and this hook reads and writes it through there.
//
// Keyed by account, not by survey, on purpose: the author picks people before
// staging a survey as often as after, and that has always worked.
//
// localStorage still holds the single linked-survey display record, which is cosmetic.
// The authoritative staged/deployed/sent lifecycle lives in surveys.status and
// is reconciled against on load. Keys are versioned (v2) and org-scoped so one
// account's cache never leaks into another's.
const NS_PREFIX = 'console.stagingArea.v2';
const MIGRATION_FLAG = 'console.stagingArea.migratedV2';

// The old localStorage-only staging keys (v1). The one-time migration clears
// these so stale, server-unknown staging can never resurrect.
const STALE_V1_KEYS = [
    'console.stagingArea.v1',
    'console.stagingArea.linkedSurveys.v1',
    'console.stagingArea.sentRecipientIds.v1',
    'console.stagingArea.recipientMeta.v1',
];

const nsKey = (orgId, suffix) => `${NS_PREFIX}.${orgId || 'default'}.${suffix}`;

// One-time migration: drop every legacy console.stagingArea.* key (the old
// localStorage-only staging) exactly once. Guarded by MIGRATION_FLAG so it runs
// a single time per browser and never touches the new v2 namespace.
function runStagingMigrationOnce() {
    try {
        if (typeof localStorage === 'undefined') return;
        if (localStorage.getItem(MIGRATION_FLAG)) return;
        STALE_V1_KEYS.forEach(k => localStorage.removeItem(k));
        for (let i = localStorage.length - 1; i >= 0; i -= 1) {
            const key = localStorage.key(i);
            if (
                key
                && key.startsWith('console.stagingArea.')
                && key !== MIGRATION_FLAG
                && !key.startsWith(`${NS_PREFIX}.`)
            ) {
                localStorage.removeItem(key);
            }
        }
        localStorage.setItem(MIGRATION_FLAG, new Date().toISOString());
    } catch {
        // Best-effort; migration failure must never block staging.
    }
}

function readJSON(key, fallback) {
    try {
        if (typeof localStorage === 'undefined') return fallback;
        const raw = localStorage.getItem(key);
        if (!raw) return fallback;
        const parsed = JSON.parse(raw);
        return parsed === null || parsed === undefined ? fallback : parsed;
    } catch {
        return fallback;
    }
}

// Convert DBAM's canonical materialization response into the only recipient
// shape accepted by ChannelGate. Staged contact metadata is intentionally not
// an input: it is presentation-only and must never reach delivery.
export function buildMaterializedOutreachRecipients(globalRecipientIds, materializedRecipients) {
    const byGlobalID = new Map((materializedRecipients || []).map(item => [item.global_recipient_id, item]));
    return globalRecipientIds.map(globalRecipientID => {
        const item = byGlobalID.get(globalRecipientID);
        if (!item || !item.recipient_id) {
            throw new Error(`Materialization omitted recipient ${globalRecipientID}`);
        }
        // Messenger DCM resolves a verified Page-scoped PSID in ChannelGate
        // using the selected sender binding. The legacy flat recipient ID is
        // neither an eligibility gate nor an outbound address.
        return {
            // ChannelGate uses this deployment recipient for both URL and Bootstrap.
            recipient_id: item.recipient_id,
            global_recipient_id: item.global_recipient_id,
            // The stored preference is deliberately NOT sent (design §3.1,
            // corrected 2026-08-10). preferred_channel is a wish list, and two
            // ChannelGate branches would route on it if it traveled: the auto
            // branch, and the SMS-link branch inside deliverBrowser. Sending
            // 'browser' starves both without touching ChannelGate. The channel
            // the batch actually goes out on is target_channel — the staging
            // area's channel context. phone_number still travels, because SMS
            // will need it when SMS becomes a real channel context.
            preferred_channel: 'browser',
            phone_number: item.phone_number || '',
            email: item.email || '',
            name: item.name || '',
            // The sam@↔recipient conversation, empty until first contact,
            // plus the Block flag — both read by ChannelGate's Google Chat
            // path (WP-CH-04).
            google_chat_dm: item.google_chat_dm || '',
            google_chat_refused: Boolean(item.google_chat_refused),
        };
    });
}

// Keep the persistence boundary testable: no outreach callback can run until
// DBAM has supplied a complete canonical mapping.
export async function dispatchAfterMaterialization({ globalRecipientIds, materialize, outreach }) {
    const materializedRecipients = await materialize();
    const recipients = buildMaterializedOutreachRecipients(globalRecipientIds, materializedRecipients);
    return outreach(recipients);
}

// One operation owns both halves of the client transition: wait for DBAM's
// atomic unstage, then refresh its authoritative staging revision/context.
export async function unstageAndRefresh({ deploymentId, unstage = unstageDeploymentInCloud, refresh }) {
    await unstage({ deploymentId });
    await refresh();
}

export function stagingReconciliationNotice(action) {
    if (action === 'attached_staged_deployment') {
        return 'Staging recovered the server-staged survey link. Your recipients and send method were preserved.';
    }
    if (action === 'cleared_orphaned_deployment') {
        return 'Staging cleared an outdated survey link. Your recipients and send method were preserved.';
    }
    return '';
}

export default function useStagingArea({ orgId = '', onError, onAfterTransition, onSentConflict, channelControls = null } = {}) {
    // Run the one-time stale-key migration before any persisted state is read.
    useEffect(() => { runStagingMigrationOnce(); }, []);

    // Stash the latest orgId and callbacks in refs so sendAll / linkSurvey read
    // the current values without recomputing their closures each render.
    const orgIdRef = useRef(orgId);
    useEffect(() => { orgIdRef.current = orgId; }, [orgId]);
    const onErrorRef = useRef(onError);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);
    const onAfterTransitionRef = useRef(onAfterTransition);
    useEffect(() => { onAfterTransitionRef.current = onAfterTransition; }, [onAfterTransition]);
    // Fallback route for the sent → re-deploy flow: if a stage attempt is
    // rejected because the survey is already 'sent' (server 409), delegate to
    // this handler instead of surfacing a dead-end error. The primary detection
    // path (drag payload lifecycleStatus) short-circuits before stage is ever
    // called; this ref is the safety net for when no reliable status field is
    // present on the drag object.
    const onSentConflictRef = useRef(onSentConflict);
    useEffect(() => { onSentConflictRef.current = onSentConflict; }, [onSentConflict]);
    const channelControlsRef = useRef(channelControls);
    useEffect(() => { channelControlsRef.current = channelControls; }, [channelControls]);

    // stagedIds and recipientMeta are read from sam-dbam, not from localStorage.
    // They are held in state so chips render without a round trip, but every
    // change goes through sam-dbam first and the answer it returns is what lands
    // here. Nothing is written locally and hoped for.
    const [stagedIds, setStagedIds] = useState(new Set());
    const [recipientMeta, setRecipientMeta] = useState({});
    // The channel context (WP-CH-01/02): the channel the staged batch goes out
    // on. Server state, mirrored here for rendering, exactly like the staged
    // list. web — the rich browser survey — is the default; there is no Auto.
    const [stagingChannel, setStagingChannelState] = useState(DEFAULT_CHANNEL);
    const [dispatchMode, setDispatchMode] = useState('channel_response');
    const [stagingRevision, setStagingRevision] = useState(0);
    const [crmOfferedChannels, setCRMOfferedChannels] = useState([]);
    const [dispatchPreview, setDispatchPreview] = useState(null);
    const [dispatchCapabilities, setDispatchCapabilities] = useState([]);
    const [sentRecipientIds, setSentRecipientIds] = useState(new Set());
    const [linkedSurvey, setLinkedSurvey] = useState(null);
    // Older presentation components still accept an array, but the hook's
    // authoritative state is one nullable deployment. There is no collection
    // here that can accidentally grow into a Cartesian-product dispatch.
    const linkedSurveys = useMemo(() => linkedSurvey ? [linkedSurvey] : [], [linkedSurvey]);
    // Notice shown when reconcile-against-server dropped stale staged entries.
    const [reconcileNotice, setReconcileNotice] = useState(null);

    // The account's caps and what it has used, read from sam-dbam. Used to show
    // the author where they stand while they pick people, before anything is
    // refused. null until the first read finishes.
    const [planUsage, setPlanUsage] = useState(null);
    // The last refusal from sam-dbam, in the plain wording it sent. The staging
    // area shows this as it is.
    const [limitNotice, setLimitNotice] = useState(null);

    // Hydrate cosmetic state from the org-scoped keys, once per org. Guards
    // against the transient first render where orgId is '' (pre-auth) followed
    // by the real org id.
    const loadedOrgRef = useRef(null);
    useEffect(() => {
        if (loadedOrgRef.current === orgId) return;
        loadedOrgRef.current = orgId;
        // stagedIds and recipientMeta are not read from localStorage any more.
        // They come from sam-dbam, in syncStagedRecipients below.
        setStagedIds(new Set());
        setRecipientMeta({});
        setSentRecipientIds(new Set(readJSON(nsKey(orgId, 'sentRecipientIds'), [])));
        const saved = readJSON(nsKey(orgId, 'linkedSurvey'), undefined);
        const predecessor = readJSON(nsKey(orgId, 'linkedSurveys'), []);
        setLinkedSurvey(saved || (Array.isArray(predecessor) ? predecessor[0] : null) || null);
    }, [orgId]);

    // ---- Plan limits ---------------------------------------------------------
    //
    // refreshPlanUsage reads the caps and the current count from sam-dbam. It
    // runs when the org is known and again after every send, so the numbers the
    // author sees are the numbers sam-dbam will enforce.
    const refreshPlanUsage = useCallback(async () => {
        if (!orgIdRef.current) return null;
        try {
            const usage = await fetchPlanUsage();
            setPlanUsage(usage || null);
            return usage || null;
        } catch (e) {
            // A failed read must not block staging. The caps still hold in
            // sam-dbam; the author simply does not get the running count.
            console.warn('[Staging] could not read plan limits:', e?.message || e);
            return null;
        }
    }, []);
    useEffect(() => { refreshPlanUsage(); }, [orgId, refreshPlanUsage]);

    const dismissLimitNotice = useCallback(() => setLimitNotice(null), []);

    // How many more people this survey can take, or null when there is no cap
    // or the numbers have not been read yet. The staging area uses it for the
    // live "20 of 20" count.
    const recipientsRemaining = planUsage?.recipients_per_survey == null
        ? null
        : Math.max(0, planUsage.recipients_per_survey - stagedIds.size);

    // ---- Send-monitor state ---------------------------------------------------
    //
    // sendInProgress  : true while the per-recipient loop is running
    // sendResult      : grows row-by-row during the send and is finalized at the
    //                   end. Holds the same shape SendResultsModal renders, so
    //                   the same state drives both the in-flight view and the
    //                   final report. Null when no send has occurred this session.
    // monitorOpen     : controls visibility of SendResultsModal. Auto-opens on
    //                   send start; the user can dismiss it and re-open from the
    //                   staging-area indicator or the Recipients dropdown.
    // cancelRequestedRef : flag the loop checks between iterations. A ref (not
    //                   state) so the loop reads the latest value without stale
    //                   closure issues.
    // inFlightRef     : guards against re-entry from double-clicking Send.
    const [sendInProgress, setSendInProgress] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [monitorOpen, setMonitorOpen] = useState(false);
    const cancelRequestedRef = useRef(false);
    const inFlightRef = useRef(false);

    // Persist cosmetic state under the org-scoped v2 keys. Writes use the latest
    // org id so account switches never cross-contaminate caches.
    const writeNS = (suffix, value) => {
        try {
            if (typeof localStorage === 'undefined') return;
            localStorage.setItem(nsKey(orgIdRef.current, suffix), JSON.stringify(value));
        } catch {
            // Ignore local persistence failures; staging still works in-memory.
        }
    };
    // stagedIds and recipientMeta are deliberately NOT written to localStorage.
    // A second copy is what let the browser and sam-dbam disagree.
    useEffect(() => { writeNS('sentRecipientIds', [...sentRecipientIds]); }, [sentRecipientIds]);
    useEffect(() => { writeNS('linkedSurvey', linkedSurvey); }, [linkedSurvey]);

    // beforeunload guard — registered only while a send is in flight. Prevents
    // accidental tab close / refresh that would interrupt the JS loop. Removed
    // on every exit path (completion, cancellation, error) by toggling
    // sendInProgress back to false.
    useEffect(() => {
        if (!sendInProgress) return undefined;
        const handler = (e) => {
            e.preventDefault();
            // Returning a string used to customize the dialog message; modern
            // browsers ignore the text but still show the native prompt as long
            // as returnValue is set.
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [sendInProgress]);

    // applyServerList replaces local state with what sam-dbam says the list is.
    // Every write path ends here, so the screen can never show a person sam-dbam
    // does not hold.
    const applyServerList = useCallback((recipients) => {
        const list = Array.isArray(recipients) ? recipients : [];
        setStagedIds(new Set(list.map(r => r.global_recipient_id)));
        setRecipientMeta(() => {
            const next = {};
            for (const r of list) {
                next[r.global_recipient_id] = {
                    displayName: r.name || r.email || r.global_recipient_id,
                    name: r.name || '',
                    email: r.email || '',
                    phone_number: r.phone_number || '',
                    preferred_channel: r.preferred_channel || 'email',
                    channel: r.preferred_channel || 'email', // legacy alias retained for chip rendering
                };
            }
            return next;
        });
    }, []);

    // syncStagedRecipients reads the staged recipients back from sam-dbam.
    const syncStagedRecipients = useCallback(async () => {
        if (!orgIdRef.current) return;
        try {
            const area = await listStagingArea();
            applyServerList(area.recipients);
            setDispatchMode(area.dispatch_mode || 'channel_response');
            setStagingChannelState(area.direct_channel || '');
            setCRMOfferedChannels(area.crm_offered_channels || []);
            setStagingRevision(area.revision || 0);
            if (area.reconciled) {
                setReconcileNotice(stagingReconciliationNotice(area.reconciliation));
            }
        } catch (e) {
            if (e?.code === 'STAGING_STATE_AMBIGUOUS') {
                setReconcileNotice(e.message);
            }
            console.warn('[Staging] could not read the staged recipients:', e?.message || e);
        }
    }, [applyServerList]);
    useEffect(() => { syncStagedRecipients(); }, [orgId, syncStagedRecipients]);
    useEffect(() => {
        let cancelled = false;
        if (!orgId) {
            setDispatchCapabilities([]);
            return undefined;
        }
        listDispatchCapabilities()
            .then((rows) => { if (!cancelled) setDispatchCapabilities(rows); })
            .catch((error) => {
                if (!cancelled) {
                    setDispatchCapabilities([]);
                    console.warn('[Staging] could not read channel capabilities:', error?.message || error);
                }
            });
        return () => { cancelled = true; };
    }, [orgId]);

    // setStagingChannel — store the channel context in sam-dbam and apply the
    // answer. The first build has no qualification check (design §3.2), so a
    // switch never drops anyone; the server simply stores the value and
    // returns the list unchanged.
    const setStagingChannel = useCallback(async (sendMethod) => {
        const nextMode = sendMethod === 'channel_response' ? 'channel_response' : 'direct_channel';
        const channel = nextMode === 'direct_channel' ? canonicalExecutionChannel(sendMethod) : '';
        if (nextMode === dispatchMode && channel === stagingChannel) return;
        const disabledReason = nextMode === 'direct_channel'
            ? productionChannelDisabledReason(channelControlsRef.current, channel)
            : '';
        if (disabledReason) {
            onErrorRef.current?.(disabledReason);
            return;
        }
        try {
            const area = await setStagingAreaContext({
                dispatch_mode: nextMode,
                deployment_id: linkedSurveys[0]?.deploymentId || null,
                direct_channel: channel || null,
                crm_offered_channels: crmOfferedChannels,
                expected_revision: stagingRevision,
            });
            applyServerList(area.recipients);
            setDispatchMode(area.dispatch_mode);
            setStagingChannelState(area.direct_channel || '');
            setCRMOfferedChannels(area.crm_offered_channels || []);
            setStagingRevision(area.revision || 0);
            setDispatchPreview(null);
        } catch (e) {
            onErrorRef.current?.(e?.message || 'Could not set the staging channel.');
            await syncStagedRecipients();
        }
    }, [dispatchMode, stagingChannel, linkedSurveys, crmOfferedChannels, stagingRevision, applyServerList, syncStagedRecipients]);

    const setCRMChannels = useCallback(async (channels) => {
        const offered = [...new Set((channels || []).map(canonicalExecutionChannel).filter(Boolean))];
        try {
            const area = await setStagingAreaContext({
                dispatch_mode: 'channel_response',
                deployment_id: linkedSurveys[0]?.deploymentId || null,
                direct_channel: null,
                crm_offered_channels: offered,
                expected_revision: stagingRevision,
            });
            applyServerList(area.recipients);
            setDispatchMode(area.dispatch_mode || 'channel_response');
            setStagingChannelState('');
            setCRMOfferedChannels(area.crm_offered_channels || []);
            setStagingRevision(area.revision || 0);
            setDispatchPreview(null);
        } catch (e) {
            onErrorRef.current?.(e?.message || 'Could not update recipient-choice channels.');
            await syncStagedRecipients();
        }
    }, [linkedSurveys, stagingRevision, applyServerList, syncStagedRecipients]);

    // add — put people in the staging area.
    //
    // No survey is needed. The staged recipients belongs to the account and applies to
    // every survey staged alongside it, so the author can pick people first and
    // drag a survey in afterwards, exactly as before.
    //
    // sam-dbam checks the plan's cap on each add and refuses the one that would
    // go over. Everyone added before that one stays added.
    const add = useCallback(async (ids, _metaList) => {
        const wanted = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
        if (wanted.length === 0) return;

        setLimitNotice(null);
        try {
            for (const id of wanted) {
                // eslint-disable-next-line no-await-in-loop
                applyServerList(await addStagingAreaRecipient({ globalRecipientId: id }));
            }
        } catch (e) {
            await syncStagedRecipients();
            if (e?.isLimitRefusal) {
                setLimitNotice(e.message);
            } else {
                onErrorRef.current?.(e?.message || 'Could not add that person.');
            }
        }
    }, [applyServerList, syncStagedRecipients]);

    const remove = useCallback(async (id) => {
        if (!id) return;
        setLimitNotice(null);
        try {
            applyServerList(await removeStagingAreaRecipient({ globalRecipientId: id }));
        } catch (e) {
            onErrorRef.current?.(e?.message || 'Could not remove that person.');
            await syncStagedRecipients();
        }
    }, [applyServerList, syncStagedRecipients]);

    const clear = useCallback(async () => {
        const ids = [...stagedIds];
        setSentRecipientIds(new Set());
        for (const id of ids) {
            // eslint-disable-next-line no-await-in-loop
            await remove(id);
        }
    }, [stagedIds, remove]);

    const getRecipientMeta = useCallback((id) => recipientMeta[id] || null, [recipientMeta]);

    // replaceAll — make the staged list exactly this set. Used by the Recipients
    // overlay, which hands back the whole selection rather than a delta.
    const replaceAll = useCallback(async (ids) => {
        const wanted = new Set((Array.isArray(ids) ? ids : []).filter(Boolean));
        const toRemove = [...stagedIds].filter(id => !wanted.has(id));
        const toAdd = [...wanted].filter(id => !stagedIds.has(id));
        for (const id of toRemove) {
            // eslint-disable-next-line no-await-in-loop
            await remove(id);
        }
        if (toAdd.length > 0) await add(toAdd);
    }, [stagedIds, add, remove]);

    // linkSurvey — Stage transition (deployed → staged), server-enforced.
    // Dragging a deployed survey into the Staging Area calls the sam-dbam Stage
    // endpoint; only on success do we add the cosmetic linked-survey record. The
    // staged recipient *selection* (stagedIds) stays client-side.
    const linkSurvey = useCallback(async (survey) => {
        if (!survey?.deploymentId) {
            onErrorRef.current?.('Cannot stage a survey without a deployment id.');
            return;
        }
        if (linkedSurveys.length > 0 && linkedSurveys[0].deploymentId !== survey.deploymentId) {
            onErrorRef.current?.('Remove the staged survey before adding another.');
            return;
        }
        setLimitNotice(null);
        try {
            await stageDeploymentInCloud({ deploymentId: survey.deploymentId });
        } catch (e) {
            // The account has used up its surveys. This is the earliest point we
            // can say so, which is the whole reason the check lives in staging:
            // the author is stopped before they pick a single recipient.
            if (e?.isLimitRefusal) {
                setLimitNotice(e.message);
                refreshPlanUsage();
                return;
            }
            // Fallback sent-detection: sam-dbam rejects staging a 'sent' survey
            // with a 409 whose message names the state. Route those into the
            // re-deploy-for-send orchestration instead of dead-ending on an error.
            const msg = String(e?.message || '');
            if ((/survey is "sent"/i.test(msg) || /must be Deployed to stage/i.test(msg)) && onSentConflictRef.current) {
                onSentConflictRef.current(survey);
                return;
            }
            onErrorRef.current?.(`Could not stage “${survey.surveyName || 'survey'}”: ${e.message}`);
            return;
        }

        // Stage and context attachment are one server-owned operation. Read
        // back its new revision instead of issuing a second context write with
        // the pre-stage revision (which correctly fails as REVIEW_STALE).
        await syncStagedRecipients();

        const nextSurvey = { ...survey, linkedAt: Date.now() };
        setLinkedSurvey(nextSurvey);
        refreshPlanUsage();
        onAfterTransitionRef.current?.();
    }, [linkedSurveys, refreshPlanUsage, syncStagedRecipients]);

    // unlinkSurvey — Unstage transition (staged → deployed), server-enforced.
    // 'x'-ing a survey out of the Staging Area returns it to Deployed. The local
    // record is removed only after the server accepts the transition.
    const unlinkSurvey = useCallback(async (deploymentId) => {
        try {
            await unstageAndRefresh({ deploymentId, refresh: syncStagedRecipients });
        } catch (e) {
            onErrorRef.current?.(`Could not unstage: ${e.message}`);
            return;
        }
        setLinkedSurvey(null);
        // Unstaging gives the survey back, so the count drops. The staged
        // recipient rows are left alone: if the author stages it again, their
        // selection is still there.
        refreshPlanUsage();
        onAfterTransitionRef.current?.();
    }, [refreshPlanUsage, syncStagedRecipients]);

    // reconcileLinked — make the Staging Area's linked-survey list DERIVE from
    // the server lifecycle state. Called by App after each load/refresh with the
    // authoritative list of currently-staged surveys ([{deploymentId, surveyId,
    // surveyName, deploymentSlug}]). It:
    //   • drops any local record the server no longer considers staged, surfacing
    //     a visible notice (WP3 §4);
    //   • adds any server-staged survey missing locally (e.g. after the one-time
    //     migration cleared the old cache), so a staged survey is never stranded;
    //   • preserves each entry's cosmetic linkedAt while refreshing server fields.
    const reconcileLinked = useCallback((stagedSurveys) => {
        const all = Array.isArray(stagedSurveys) ? stagedSurveys : [];
        const list = all.slice(0, 1);
        if (all.length > 1) {
            setReconcileNotice('More than one predecessor survey is marked Staged. Remove the staged survey before adding another; only one is shown for dispatch.');
        }
        const stagedSet = new Set(list.map(s => s?.deploymentId).filter(Boolean));
        setLinkedSurvey(prev => {
            const previous = prev ? [prev] : [];
            const { dropped } = reconcileStagedSurveys(previous, stagedSet);
            if (dropped.length > 0) {
                const names = dropped.map(s => s?.surveyName || s?.deploymentId).filter(Boolean);
                setReconcileNotice(
                    `${dropped.length} staged survey${dropped.length === 1 ? '' : 's'} `
                    + `(${names.join(', ')}) ${dropped.length === 1 ? 'is' : 'are'} no longer staged `
                    + `on the server and ${dropped.length === 1 ? 'was' : 'were'} removed from the Staging Area.`
                );
            }
            const next = list[0];
            if (!next) return null;
            if (prev?.deploymentId === next.deploymentId) {
                return { ...next, linkedAt: prev.linkedAt || Date.now() };
            }
            return { ...next, linkedAt: Date.now() };
        });
        // The staged recipients belongs to sam-dbam now, so read it back
        // whenever the staged-survey set is reconciled. This is what replaces
        // the old stale-entry problem: there is only one copy to be right.
        syncStagedRecipients();
        refreshPlanUsage();
    }, [syncStagedRecipients, refreshPlanUsage]);

    const dismissReconcileNotice = useCallback(() => setReconcileNotice(null), []);

    // Monitor controls
    const openSendMonitor = useCallback(() => setMonitorOpen(true), []);
    const closeSendMonitor = useCallback(() => setMonitorOpen(false), []);
    const dismissSendResult = useCallback(() => {
        setMonitorOpen(false);
        setSendResult(null);
    }, []);
    const cancelSend = useCallback(() => {
        if (!inFlightRef.current) return;
        cancelRequestedRef.current = true;
    }, []);

    // sendAll — kicks off the per-recipient send loop and returns immediately.
    // The loop runs as an inner async IIFE; UI components observe progress via
    // sendInProgress / sendResult / monitorOpen state, not via the returned
    // promise. The promise is still returned (resolves when the loop ends) for
    // callers that want to chain follow-up work.
    //
    // Identity rebuild on send: before any /outreach calls fire, the loop
    // re-fetches the deployments list from sam-dbam and re-resolves each
    // linked survey's canonical deploy_slug by UUID. The localStorage-persisted
    // The linked-survey record is treated as a memo of "the user picked this
    // deployment by UUID" — every other identity field (slug, etc.) is
    // re-derived from the fresh server response at the moment of Send. This
    // protects against stale linked-survey records (drag captured before a
    // field was added; deployment archived between linking and sending; etc.)
    // and ensures the routing identity used by ChannelGate is always current.
    // Retained for DCM while each existing provider adapter is moved onto the
    // durable queue. CRM confirmation never enters this browser-owned loop.
    const legacySendAll = useCallback(() => {
        // Guard against double-click re-entry. A send already in flight cannot
        // be re-started; the existing loop continues.
        if (inFlightRef.current) {
            return Promise.resolve(sendResult);
        }
        if (linkedSurveys.length === 0 || stagedIds.size === 0) {
            return Promise.resolve(null);
        }

        const disabledReason = productionChannelDisabledReason(
            channelControlsRef.current,
            stagingChannel || DEFAULT_CHANNEL,
        );
        if (disabledReason) {
            onErrorRef.current?.(disabledReason);
            return Promise.resolve(null);
        }

        const stagedIdList = [...stagedIds];
        // The batch channel, captured once at loop start so a mid-send switch
        // cannot split a batch across channels (design §3.3).
        const batchChannel = canonicalControlChannel(stagingChannel || DEFAULT_CHANNEL);
        const sentAt = new Date().toISOString();
        console.log('[Staging] sendAll dispatching', linkedSurveys.length, 'surveys to', stagedIdList.length, 'recipients (per-recipient loop, pre-flight rebuild)');

        // Staging is transient. DBAM replaces this local presentation data with
        // canonical contacts and a deployment-scoped recipient identity before
        // any ChannelGate request is allowed to run.
        const stagedRecipients = stagedIdList.map(id => {
            const meta = recipientMeta[id] || {};
            return {
                recipient_id: id,
                preferred_channel: meta.preferred_channel || meta.channel || 'browser',
                phone_number: meta.phone_number || '',
                email: meta.email || '',
                name: meta.name || meta.displayName || '',
            };
        });

        const totalOverall = linkedSurveys.length * stagedRecipients.length;

        // Seed sendResult with empty per-survey blocks. The loop fills the
        // recipients arrays row by row.
        const initial = {
            sentAt,
            channel: batchChannel,
            totalOverall,
            doneOverall: 0,
            currentRecipient: null,
            currentSurveyIndex: 0,
            cancelled: false,
            complete: false,
            // Seed slug as empty — the pre-flight rebuild inside runLoop
            // resolves the canonical slug from sam-dbam before any /outreach
            // calls fire, then patches it into each survey block.
            surveys: linkedSurveys.map(s => ({
                deploymentId: s.deploymentId,
                deploymentSlug: '',
                surveyName: s.surveyName,
                ok: null,
                error: null,
                recipients: [],
            })),
        };

        cancelRequestedRef.current = false;
        inFlightRef.current = true;
        setSendResult(initial);
        setSendInProgress(true);
        setMonitorOpen(true);

        // Helper — append a recipient row to a given survey and bump doneOverall.
        const appendRow = (surveyIdx, row) => {
            setSendResult(prev => {
                if (!prev) return prev;
                const surveys = prev.surveys.slice();
                const block = { ...surveys[surveyIdx], recipients: [...surveys[surveyIdx].recipients, row] };
                surveys[surveyIdx] = block;
                return { ...prev, surveys, doneOverall: prev.doneOverall + 1 };
            });
        };
        const setCurrentRecipient = (surveyIdx, recipient) => {
            setSendResult(prev => prev ? { ...prev, currentSurveyIndex: surveyIdx, currentRecipient: recipient } : prev);
        };
        const finalizeSurvey = (surveyIdx, ok, surveyError) => {
            setSendResult(prev => {
                if (!prev) return prev;
                const surveys = prev.surveys.slice();
                surveys[surveyIdx] = { ...surveys[surveyIdx], ok, error: surveyError || null };
                return { ...prev, surveys };
            });
        };

        const runLoop = async () => {
            const succeeded = new Set();
            let confirmedAny = false;
            let emailLease = null;
            try {
                // Every survey communication may use email either as its main
                // invitation or as the browser-launch fallback. Refuse to start
                // unless sam-dbam has made email settings read-only for this
                // short loop.
                emailLease = await acquireEmailSendLease({
                    holder: `console-send:${sentAt}`,
                    deploymentId: linkedSurveys.length === 1 ? linkedSurveys[0].deploymentId : null,
                    ttlSeconds: 600,
                });
                // ---- Pre-flight rebuild ------------------------------------
                // Fetch the current deployments list from sam-dbam and build a
                // UUID → deployment lookup. This is the authoritative source
                // for slug and identity at the moment of Send.
                let canonicalById = new Map();
                try {
                    const fresh = await listDeploymentsFromCloud({ orgId: orgIdRef.current || '' });
                    for (const d of (fresh || [])) {
                        if (d && d.deployment_id) canonicalById.set(d.deployment_id, d);
                    }
                    // Patch each survey block with the canonical slug before
                    // any rows render. Surveys whose deployment is missing
                    // from the fresh data will have their block flagged when
                    // the main loop hits them.
                    setSendResult(prev => {
                        if (!prev) return prev;
                        const surveys = prev.surveys.map((block, idx) => {
                            const canonical = canonicalById.get(linkedSurveys[idx]?.deploymentId);
                            return canonical
                                ? { ...block, deploymentSlug: canonical.deploy_slug || '' }
                                : block;
                        });
                        return { ...prev, surveys };
                    });
                } catch (preflightErr) {
                    // Pre-flight fetch failed. Abort: surface the error on
                    // every linked survey and fire zero /outreach calls. This
                    // is safer than sending with potentially-stale local data.
                    const errMsg = `Pre-flight rebuild failed (could not fetch deployments): ${preflightErr?.message || String(preflightErr)}`;
                    console.error('[Staging]', errMsg);
                    for (let si = 0; si < linkedSurveys.length; si += 1) {
                        for (const r of stagedRecipients) {
                            appendRow(si, {
                                recipient_id: r.recipient_id,
                                email: r.email,
                                name: r.name,
                                channel: r.preferred_channel,
                                status: 'failed',
                                url: '',
                                error: errMsg,
                            });
                        }
                        finalizeSurvey(si, false, 'Pre-flight rebuild failed');
                    }
                    setSendResult(prev => prev ? {
                        ...prev,
                        complete: true,
                        cancelled: false,
                        currentRecipient: null,
                    } : prev);
                    return undefined; // skip the main loop entirely
                }

                // Validate every deployment before creating any permanent
                // roster. This is a read-only canonical preflight boundary.
                const preflightInvalid = new Set();
                for (let si = 0; si < linkedSurveys.length; si += 1) {
                    const survey = linkedSurveys[si];
                    const canonical = canonicalById.get(survey.deploymentId);
                    const slug = canonical ? canonical.deploy_slug : null;
                    const preflightError = !canonical
                        ? `Deployment ${survey.deploymentId} not found in current data — was it archived or deleted?`
                        : !slug ? 'Missing deployment slug'
                            : canonical.is_open !== true ? 'Deployment is closed — re-deploy before sending' : '';
                    if (preflightError) {
                        preflightInvalid.add(si);
                        for (const r of stagedRecipients) {
                            appendRow(si, { recipient_id: r.recipient_id, email: r.email, name: r.name, channel: r.preferred_channel, status: 'failed', url: '', error: preflightError });
                        }
                        finalizeSurvey(si, false, preflightError);
                    }
                }
                for (let si = 0; si < linkedSurveys.length; si += 1) {
                    if (preflightInvalid.has(si)) continue;
                    const survey = linkedSurveys[si];
                    const canonical = canonicalById.get(survey.deploymentId);
                    const slug = canonical.deploy_slug;
                    // Cancellation before this deployment has a durable roster
                    // must not create one merely to report cancellation.
                    if (cancelRequestedRef.current) {
                        for (const r of stagedRecipients) {
                            appendRow(si, { recipient_id: r.recipient_id, email: r.email, name: r.name, channel: r.preferred_channel, status: 'cancelled', url: '', error: 'cancelled by user' });
                        }
                        finalizeSurvey(si, false, 'cancelled by user');
                        continue;
                    }
                    let outreachRecipients;
                    try {
                        // Confirm the immutable roster and staged → sent state
                        // in DBAM immediately before the first outreach. This
                        // must succeed before ChannelGate can be called.
                        // No recipient list is sent. sam-dbam uses the staged
                        // list it already holds, which staging built under the
                        // plan's cap.
                        const confirmed = await sendDeploymentInCloud({
                            deploymentId: survey.deploymentId,
                        });
                        confirmedAny = true;
                        await dispatchAfterMaterialization({
                            globalRecipientIds: stagedIdList,
                            materialize: async () => confirmed.recipients,
                            // This assigns the canonical, deployment-scoped
                            // batch consumed by the following sendOutreach loop.
                            outreach: async recipients => { outreachRecipients = recipients; },
                        });
                    } catch (materializeErr) {
                        const errMsg = `Send confirmation failed: ${materializeErr?.message || String(materializeErr)}`;
                        for (const r of stagedRecipients) {
                            appendRow(si, { recipient_id: r.recipient_id, email: r.email, name: r.name, channel: r.preferred_channel, status: 'failed', url: '', error: errMsg });
                        }
                        finalizeSurvey(si, false, 'Send confirmation failed');
                        continue;
                    }
                    let surveyHadFailure = false;
                    for (let ri = 0; ri < outreachRecipients.length; ri += 1) {
                        if (cancelRequestedRef.current) {
                            // Fill remaining recipients in this survey as cancelled.
                            for (let rj = ri; rj < outreachRecipients.length; rj += 1) {
                                const r = outreachRecipients[rj];
                                appendRow(si, {
                                    recipient_id: r.global_recipient_id,
                                    email: r.email,
                                    name: r.name,
                                    channel: r.preferred_channel,
                                    status: 'cancelled',
                                    url: '',
                                    error: 'cancelled by user',
                                });
                            }
                            break;
                        }

                        const r = outreachRecipients[ri];
                        setCurrentRecipient(si, { recipient_id: r.global_recipient_id, name: r.name, email: r.email });

                        let row;
                        try {
                            // One call per recipient — the unit of progress.
                            // No template ID: ChannelGate sends the org's saved
                            // invitation copy from Channels → Bindings → Templates. This
                            // screen has no template picker, and it must not
                            // silently downgrade to the platform wording.
                            const resp = await sendOutreach(slug, batchChannel, [r]);
                            const respResults = Array.isArray(resp?.results) ? resp.results : [];
                            const rr = respResults[0] || {};
                            row = {
                                recipient_id: r.global_recipient_id,
                                email: r.email,
                                name: r.name,
                                channel: rr.channel || r.preferred_channel || '',
                                status: rr.status || 'failed',
                                url: rr.url || '',
                                error: rr.error || '',
                                notification_channel: rr.notification_channel || '',
                            };
                            if (row.status === 'sent' || row.status === 'prepared') {
                                succeeded.add(r.global_recipient_id);
                            } else {
                                surveyHadFailure = true;
                            }
                        } catch (err) {
                            // HTTP-level failure (network, 5xx, etc.) — record
                            // and continue to the next recipient. We do not
                            // abort the whole loop on a single failure; if the
                            // user wants to stop, they click Stop sending.
                            const errMsg = err?.message || String(err);
                            console.error('[Staging] sendOutreach failed for', slug, r.recipient_id, err);
                            row = {
                                recipient_id: r.global_recipient_id,
                                email: r.email,
                                name: r.name,
                                channel: r.preferred_channel,
                                status: 'failed',
                                url: '',
                                error: errMsg,
                            };
                            surveyHadFailure = true;
                        }
                        appendRow(si, row);
                    }

                    finalizeSurvey(si, !surveyHadFailure && !cancelRequestedRef.current, null);

                    if (cancelRequestedRef.current) {
                        // Fill any remaining (un-touched) surveys as all-cancelled.
                        for (let sj = si + 1; sj < linkedSurveys.length; sj += 1) {
                            if (preflightInvalid.has(sj)) continue;
                            for (const r of stagedRecipients) {
                                appendRow(sj, {
                                    recipient_id: r.recipient_id,
                                    email: r.email,
                                    name: r.name,
                                    channel: r.preferred_channel,
                                    status: 'cancelled',
                                    url: '',
                                    error: 'cancelled by user',
                                });
                            }
                            finalizeSurvey(sj, false, 'cancelled by user');
                        }
                        break;
                    }
                }

                if (succeeded.size > 0) {
                    setSentRecipientIds(prev => {
                        const next = new Set(prev);
                        succeeded.forEach(id => next.add(id));
                        return next;
                    });
                }

                // The lifecycle transition happened before outreach as part of
                // the atomic confirmation above. Refresh parent state only once
                // after the loop; delivery outcomes never rewrite the roster.
                if (confirmedAny) onAfterTransitionRef.current?.();
                // A send uses up part of the allowance, so re-read the count.
                refreshPlanUsage();

                setSendResult(prev => prev ? {
                    ...prev,
                    complete: true,
                    cancelled: cancelRequestedRef.current,
                    currentRecipient: null,
                } : prev);
            } catch (runErr) {
                const message = runErr?.message || String(runErr);
                console.error('[Staging] send stopped before completion:', runErr);
                setSendResult(prev => prev ? {
                    ...prev,
                    complete: true,
                    cancelled: false,
                    currentRecipient: null,
                    error: message,
                } : prev);
                onErrorRef.current?.(message);
            } finally {
                if (emailLease?.lease_id) {
                    try {
                        await releaseEmailSendLease(emailLease.lease_id);
                    } catch (releaseErr) {
                        // Expiry is the recovery path. The send result remains
                        // truthful even when cleanup needs that safety net.
                        console.warn('[Staging] email settings lock will expire automatically:', releaseErr?.message || releaseErr);
                    }
                }
                inFlightRef.current = false;
                cancelRequestedRef.current = false;
                setSendInProgress(false);
            }
            return undefined;
        };

        return runLoop();
    }, [linkedSurveys, stagedIds, recipientMeta, sendResult, refreshPlanUsage, stagingChannel]);

    const prepareSendReview = useCallback(async () => {
        if (linkedSurveys.length !== 1 || stagedIds.size === 0) return null;
        const preview = await previewDispatch();
        setDispatchPreview(preview);
        return preview;
    }, [linkedSurveys, stagedIds]);

    const sendAll = useCallback(async (review = dispatchPreview) => {
        if (inFlightRef.current) return sendResult;
        if (linkedSurveys.length !== 1 || stagedIds.size === 0) return null;

        // CRM is handed to the durable server queue. Existing DCM adapters are
        // retained on their proven direct-send path until each is migrated to
        // the same worker contract; this avoids confirming work that no worker
        // can yet deliver.
        if (dispatchMode === 'direct_channel') return legacySendAll();

        inFlightRef.current = true;
        setSendInProgress(true);
        try {
            const currentReview = review || await previewDispatch();
            setDispatchPreview(currentReview);
            if (!currentReview?.ready) {
                throw new Error((currentReview?.blocking_reasons || []).join(' ') || 'This dispatch is not ready to send.');
            }
            const batch = await confirmDispatch({
                previewHash: currentReview.preview_hash,
                stagingRevision: currentReview.staging_revision,
            });
            const queuedRecipients = (currentReview.recipients || []).filter((recipient) => recipient.eligible);
            const result = {
                sentAt: batch.confirmed_at || new Date().toISOString(),
                dispatchBatchId: batch.dispatch_batch_id,
                channel: currentReview.dispatch_mode === 'channel_response' ? 'channel_response' : currentReview.direct_channel,
                totalOverall: queuedRecipients.length,
                doneOverall: queuedRecipients.length,
                complete: true,
                acceptedStage: 'queued',
                surveys: [{
                    deploymentId: currentReview.deployment_id,
                    deploymentSlug: currentReview.deploy_slug,
                    surveyName: currentReview.survey_title,
                    ok: true,
                    recipients: queuedRecipients.map((recipient) => ({
                        recipient_id: recipient.global_recipient_id,
                        email: recipient.email,
                        name: recipient.name || '',
                        channel: currentReview.dispatch_mode === 'channel_response' ? 'email' : currentReview.direct_channel,
                        notification_channel: currentReview.dispatch_mode === 'channel_response' || currentReview.direct_channel === 'browser' ? 'email' : '',
                        status: 'queued',
                        error: '',
                    })),
                }],
            };
            setSendResult(result);
            setMonitorOpen(true);
            setLinkedSurvey(null);
            setDispatchPreview(null);
            onAfterTransitionRef.current?.();
            refreshPlanUsage();
            return result;
        } catch (error) {
            if (error?.code === 'REVIEW_STALE' || /REVIEW_STALE/.test(String(error?.message || ''))) {
                setDispatchPreview(null);
            }
            onErrorRef.current?.(error?.message || 'Could not confirm the dispatch.');
            throw error;
        } finally {
            inFlightRef.current = false;
            setSendInProgress(false);
        }
    }, [dispatchPreview, linkedSurveys, stagedIds, sendResult, refreshPlanUsage, dispatchMode, legacySendAll]);

    const getSentStatus = useCallback((recipientId) => {
        if (linkedSurveys.length === 0) return 'neutral';
        return sentRecipientIds.has(recipientId) ? 'linked-and-sent' : 'linked-only';
    }, [linkedSurveys, sentRecipientIds]);

    return {
        stagedIds,
        sentRecipientIds,
        recipientMeta,
        // The channel context — read for the selector, written through
        // sam-dbam. web by default; never Auto.
        stagingChannel,
        dispatchMode,
        crmOfferedChannels,
        stagingRevision,
        dispatchPreview,
        dispatchCapabilities,
        setStagingChannel,
        setCRMChannels,
        prepareSendReview,
        add,
        remove,
        clear,
        replaceAll,
        count: stagedIds.size,
        linkedSurveys,
        linkSurvey,
        unlinkSurvey,
        sendAll,
        getSentStatus,
        getRecipientMeta,
        // Server-state reconciliation surface (WP3)
        reconcileLinked,
        reconcileNotice,
        dismissReconcileNotice,
        // Plan limits. planUsage holds the caps and the running count;
        // limitNotice holds sam-dbam's refusal in the wording it sent, ready to
        // show as it is.
        planUsage,
        recipientsRemaining,
        limitNotice,
        dismissLimitNotice,
        refreshPlanUsage,
        // Send monitor surface
        sendInProgress,
        sendResult,
        monitorOpen,
        openSendMonitor,
        closeSendMonitor,
        dismissSendResult,
        cancelSend,
    };
}
